/**
 * Shared chrome for the `(setup)` route group (ADR 0006).
 *
 * Setup is a route group rather than a stack of blocking overlays, so every
 * step screen owns the whole viewport and needs the same frame: safe area,
 * a header, progress dots, a scrollable content region, and a pinned footer
 * action. `SetupLayout` owns all of it so each step screen stays pure content.
 *
 * Two details are deliberate:
 *  - Log out is on EVERY screen. Once setup owns the whole screen, a user stuck
 *    on an unavailable handle or the wrong Google account has no other exit but
 *    reinstalling. This mirrors the escape hatch in `PolicyAcceptanceGate`.
 *  - `step` is optional and the dots are omitted ENTIRELY when it is absent.
 *    The username screen sits outside the 1..6 counter, so "step 3 of 6" means
 *    the same thing for the social cohort (7 screens) and the email cohort (6).
 *
 * The centered column lives HERE rather than in `ResponsiveShell`: the app shell
 * deliberately passes the setup gate straight through (ADR 0006), so setup has
 * to clamp its own width the same way `AuthScreenLayout` does -- and at the same
 * `FORM_MAX_WIDTH`, so the column never resizes between sign-up and step 6.
 */

import Ionicons from '@expo/vector-icons/Ionicons';
import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import { SafeAreaView } from 'react-native-safe-area-context';

import { FORM_MAX_WIDTH, centeredContent } from '@/components/layout/content-width';
import {
  KEYBOARD_CLEARANCE,
  KeyboardAwareScreenScroll,
} from '@/components/layout/keyboard-aware-screen-scroll';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';

/** Steps in the guided onboarding experience the dots count against. */
const DEFAULT_TOTAL_STEPS = 6;

/** Touch slack around the small header affordances (4px spacing unit x 3). */
const HEADER_HIT_SLOP = 12;

export interface SetupLayoutProps {
  /** 1-based position in the dot counter. Omit entirely to hide the dots. */
  step?: number;
  totalSteps?: number;
  /** Omit to hide the Back affordance. */
  onBack?: () => void;
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  /** Rendered only together with `onSecondary` (e.g. "Skip for now"). */
  secondaryLabel?: string;
  onSecondary?: () => void;
  children: React.ReactNode;
}

export function SetupLayout({
  step,
  totalSteps = DEFAULT_TOTAL_STEPS,
  onBack,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  secondaryLabel,
  onSecondary,
  children,
}: SetupLayoutProps) {
  const { logout } = useAuth();
  const [footerHeight, setFooterHeight] = React.useState(0);

  const handleFooterLayout = React.useCallback(
    (event: { nativeEvent: { layout: { height: number } } }) => {
      setFooterHeight(event.nativeEvent.layout.height);
    },
    []
  );

  return (
    <SafeAreaView
      testID="setup-layout"
      className="flex-1 bg-background"
      edges={['top', 'bottom']}
    >
      {/* One column around the header, dots, scroll region AND footer. Clamping
          only the content slot would leave the header and the footer's border
          running edge-to-edge while the middle centred itself. */}
      <View testID="setup-column" style={styles.column}>
        <View className="flex-row items-center justify-between px-5 py-2">
          {onBack === undefined ? (
            <View />
          ) : (
            <Button
              testID="setup-back-button"
              variant="ghost"
              size="sm"
              className="-ml-3 gap-1"
              onPress={onBack}
              accessibilityLabel="Go back"
              hitSlop={HEADER_HIT_SLOP}
            >
              <Ionicons name="chevron-back" size={20} className="text-foreground" />
              <Text className="text-base text-foreground">Back</Text>
            </Button>
          )}

          <Button
            testID="setup-logout-button"
            variant="ghost"
            size="sm"
            className="-mr-3"
            onPress={logout}
            accessibilityLabel="Log out"
            hitSlop={HEADER_HIT_SLOP}
          >
            <Text className="text-sm text-muted-foreground">Log out</Text>
          </Button>
        </View>

        {step !== undefined && (
          <ProgressDots step={step} totalSteps={totalSteps} />
        )}

        <KeyboardAwareScreenScroll
          testID="setup-content"
          bottomOffset={footerHeight + KEYBOARD_CLEARANCE}
          contentContainerStyle={styles.contentContainer}
        >
          <View className="flex-1 px-5 pb-6">{children}</View>
        </KeyboardAwareScreenScroll>

        {/* The footer rides a KeyboardStickyView so the primary action lifts above
            the keyboard instead of being covered by it, and measures itself so the
            scroll region can keep a focused field clear of it. */}
        <KeyboardStickyView testID="setup-footer" onLayout={handleFooterLayout}>
          <View className="gap-2 border-t border-border bg-background px-5 pb-2 pt-3">
            <Button
              testID="setup-primary-action"
              size="lg"
              fullWidth
              onPress={onPrimary}
              disabled={primaryDisabled}
              loading={primaryLoading}
            >
              {primaryLabel}
            </Button>

            {secondaryLabel !== undefined && onSecondary !== undefined && (
              <Button
                testID="setup-secondary-action"
                variant="ghost"
                fullWidth
                onPress={onSecondary}
              >
                {secondaryLabel}
              </Button>
            )}
          </View>
        </KeyboardStickyView>
      </View>
    </SafeAreaView>
  );
}

function ProgressDots({ step, totalSteps }: { step: number; totalSteps: number }) {
  return (
    <View
      testID="setup-progress"
      accessibilityRole="progressbar"
      className="flex-row items-center justify-center gap-2 py-4"
    >
      {Array.from({ length: totalSteps }, (_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === step;
        return (
          <View
            key={stepNumber}
            testID={`setup-step-dot-${stepNumber}`}
            accessibilityLabel={
              isActive
                ? `Step ${stepNumber} of ${totalSteps} (current)`
                : `Step ${stepNumber} of ${totalSteps}`
            }
            className={cn(
              'h-2 w-2 rounded-full',
              isActive ? 'bg-primary' : 'bg-muted-foreground/30'
            )}
          />
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  // The centred, clamped frame. `flex: 1` keeps the footer pinned to the bottom
  // of the viewport rather than to the bottom of the content.
  column: {
    flex: 1,
    ...centeredContent(FORM_MAX_WIDTH),
  },
  // Lets a short step's content stretch to fill the scroll viewport so screens
  // can centre or bottom-align their own content.
  contentContainer: {
    flexGrow: 1,
  },
});
